import WarningIcon from "@redotech/redo-icons/alert-circle.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { JSXElementConstructor, memo, ReactNode } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { BgColorValue, BorderRadiusValue, SpacingValue } from "../../theme/box";
import { TextSizeValue } from "../../theme/typography";
import { RedoButton, RedoButtonHierarchy } from "../buttons/redo-button";
import {
  FeaturedIconColor,
  FeaturedIconSize,
  RedoFeaturedIcon,
} from "../featured-icon/redo-featured-icon";
import * as css from "./redo-alert.module.css";

export const alertColor = [
  "default",
  "brand",
  "gray",
  "error",
  "warning",
  "success",
] as const;
export type AlertColor = (typeof alertColor)[number];

export const alertStyle = ["floating"] as const;
export type AlertStyle = (typeof alertStyle)[number];

export const alertSize = ["sm", "md"] as const;
export type AlertSize = (typeof alertSize)[number];

export interface AlertProps {
  title: string;
  description?: string;
  Icon?: JSXElementConstructor<any> | null;
  color?: AlertColor;
  size?: AlertSize;
  actions?: {
    text: string;
    action: () => void;
    hierarchy?: RedoButtonHierarchy;
  }[];
  /** "below" stacks actions under the text; "trailing" pins them to the right edge. */
  actionsPlacement?: "below" | "trailing";
  /** Extra content rendered under the description. */
  children?: ReactNode;
  closeButton?:
    | { showCloseButton: false }
    | { showCloseButton: true; onClose: () => void };
  fullWidth?: boolean;
  dir?: "row" | "column";
  align?: "center" | "flex-start" | "flex-end" | "stretch" | "baseline";
  borderRadius?: BorderRadiusValue;
  bgColor?: BgColorValue;
  uiSelector?: string;
}

interface SizeTokens {
  padding: SpacingValue;
  outerGap: SpacingValue;
  innerGap: SpacingValue;
  textStackGap: SpacingValue;
  iconSize: FeaturedIconSize;
  titleSize: TextSizeValue;
  descriptionSize: TextSizeValue;
  defaultBorderRadius: BorderRadiusValue;
}

const sizeTokens: Record<AlertSize, SizeTokens> = {
  sm: {
    padding: "md",
    outerGap: "md",
    innerGap: "md",
    textStackGap: "xxs",
    iconSize: "xs",
    titleSize: "xs",
    descriptionSize: "xs",
    defaultBorderRadius: "lg",
  },
  md: {
    padding: "xl",
    outerGap: "xl",
    innerGap: "xl",
    textStackGap: "md",
    iconSize: "sm",
    titleSize: "sm",
    descriptionSize: "sm",
    defaultBorderRadius: "2xl",
  },
};

// TODO: IMPLEMENT FULL WIDTH AND ADD BUTTONS
export const RedoAlert = memo(function RedoAlert({
  title,
  description,
  Icon = WarningIcon,
  color = "default",
  size = "md",
  actions,
  actionsPlacement = "below",
  children,
  closeButton,
  fullWidth = false,
  dir = "row",
  align,
  borderRadius,
  bgColor = "primary-alt",
  uiSelector,
}: AlertProps) {
  const tokens = sizeTokens[size];
  return (
    <Flex
      bgColor={bgColor}
      borderColor="primary"
      borderRadius={borderRadius ?? tokens.defaultBorderRadius}
      borderStyle="solid"
      borderWidth="1px"
      data-arbiter="RedoAlert"
      data-ui={uiSelector}
      gap={tokens.outerGap}
      p={tokens.padding}
      position="relative"
      w={fullWidth ? "full" : undefined}
    >
      {closeButton?.showCloseButton === true ? (
        <Flex className={css.closeButton}>
          <RedoButton
            aria-label="Close"
            hierarchy="tertiary"
            IconLeading={CloseIcon}
            onClick={closeButton.onClose}
          />
        </Flex>
      ) : null}
      <Flex
        align={align ?? (dir === "row" ? "center" : "flex-start")}
        dir={dir}
        gap={tokens.innerGap}
        grow={actionsPlacement === "trailing" ? 1 : undefined}
      >
        {Icon ? (
          <Flex alignSelf={dir === "column" ? "flex-start" : undefined}>
            <RedoFeaturedIcon
              color={alertColorToFeaturedIcon[color]}
              Icon={Icon}
              size={tokens.iconSize}
            />
          </Flex>
        ) : null}
        <Flex
          align="flex-start"
          dir="column"
          gap={tokens.textStackGap}
          grow={1}
        >
          <Text
            fontSize={tokens.titleSize}
            fontWeight="semibold"
            textColor="secondary"
          >
            {title}
          </Text>
          {description ? (
            <Text
              fontSize={tokens.descriptionSize}
              fontWeight="regular"
              textColor="tertiary"
            >
              {description}
            </Text>
          ) : null}
          {children}
          {actions && actionsPlacement === "below" ? (
            <Flex>
              {actions.map((action) => (
                <RedoButton
                  hierarchy={action.hierarchy ?? "tertiary"}
                  key={action.text}
                  onClick={action.action}
                  text={action.text}
                />
              ))}
            </Flex>
          ) : null}
        </Flex>
        {actions && actionsPlacement === "trailing" ? (
          <Flex align="center" gap="md" shrink={0}>
            {actions.map((action) => (
              <RedoButton
                hierarchy={action.hierarchy ?? "tertiary"}
                key={action.text}
                onClick={action.action}
                text={action.text}
              />
            ))}
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
});

const alertColorToFeaturedIcon: Record<AlertColor, FeaturedIconColor> = {
  default: "black",
  brand: "brand",
  gray: "gray",
  error: "error",
  warning: "warning",
  success: "success",
};
